<template>
  <section id="precios">
    <div class="wrap">
      <div class="section-head">
        <div class="left">
          <span class="eyebrow"><span class="dot"></span>{{ labelsState['lv_pricing_eyebrow'] || 'Pricing' }}</span>
          <h2>{{ labelsState['lv_pricing_heading_pre'] || 'Transparent.' }} <span class="grad-text">{{ labelsState['lv_pricing_heading_grad'] || 'No surprises.' }}</span></h2>
        </div>
        <div class="right">
          <div class="pricing-toggle">
            <span :class="annual ? 'toggle-label-inactive' : 'toggle-label-active'">{{ labelsState['lv_pricing_monthly'] || 'Monthly' }}</span>
            <button
              :class="['toggle-switch', { active: annual }]"
              @click="annual = !annual"
              aria-label="Toggle billing period"
              type="button"
            >
              <span class="toggle-thumb"></span>
            </button>
            <span :class="annual ? 'toggle-label-active' : 'toggle-label-inactive'">
              {{ labelsState['lv_pricing_annual'] || 'Annual' }} <span class="discount-badge">−20%</span>
            </span>
          </div>
        </div>
      </div>

      <div v-if="loading" style="text-align:center; padding:60px 0; color:var(--t-2)">
        {{ labelsState['loading'] || 'Loading plans...' }}
      </div>

      <div v-else-if="displayPlans.length" class="pricing-grid">
        <article
          v-for="(plan, i) in displayPlans"
          :key="plan.id || i"
          :class="['plan-v2', { featured: plan.is_highlighted }]"
        >
          <div class="plan-v2-name">{{ plan.subscription_plan }}</div>
          <div class="plan-v2-tag">{{ plan.is_free ? (labelsState['lv_plan_tag_free'] || 'To get started') : plan.is_enterprise ? (labelsState['lv_plan_tag_enterprise'] || 'For large teams') : (labelsState['lv_plan_tag_popular'] || 'Most popular') }}</div>
          <div class="plan-v2-price">
            <template v-if="plan.is_free || !plan.amount">
              <span class="amount" style="font-size:42px">{{ labelsState['lv_plan_free_price'] || 'Free' }}</span>
            </template>
            <template v-else-if="plan.is_enterprise">
              <span class="amount" style="font-size:42px">{{ labelsState['lv_plan_enterprise_price'] || 'Custom' }}</span>
            </template>
            <template v-else>
              <span class="currency">$</span>
              <span class="amount">{{ formatPrice(plan) }}</span>
              <span class="period">{{ labelsState['lv_plan_period'] || '/mo USD' }}</span>
            </template>
          </div>
          <p v-if="plan.description" v-html="plan.description" style="font-size:13px; color:var(--t-2); margin-top:8px"></p>
          <ul style="margin-top:20px">
            <li v-if="plan.credits">
              <svg class="check-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" width="16" height="16"><path d="M20 6L9 17l-5-5"/></svg>
              {{ formatNumber(plan.credits) }} {{ labelsState['lv_plan_credits'] || 'AI credits' }}
            </li>
            <li>
              <svg class="check-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" width="16" height="16"><path d="M20 6L9 17l-5-5"/></svg>
              {{ labelsState['lv_plan_all_channels'] || 'All channels' }}
            </li>
            <li>
              <svg class="check-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" width="16" height="16"><path d="M20 6L9 17l-5-5"/></svg>
              {{ labelsState['lv_plan_dashboard'] || 'Real-time dashboard' }}
            </li>
            <li v-if="!plan.is_free">
              <svg class="check-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" width="16" height="16"><path d="M20 6L9 17l-5-5"/></svg>
              {{ labelsState['lv_plan_handover'] || 'Human handover' }}
            </li>
            <li v-if="plan.is_enterprise">
              <svg class="check-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" width="16" height="16"><path d="M20 6L9 17l-5-5"/></svg>
              {{ labelsState['lv_plan_sla'] || 'SLA 99.9% + dedicated support' }}
            </li>
          </ul>
          <a
            :class="['btn', plan.is_highlighted ? 'btn-primary' : 'btn-ghost']"
            :href="subsUrl"
            target="_blank"
          >
            {{ plan.is_free ? (labelsState['lv_plan_btn_free'] || 'Start free') : plan.is_enterprise ? (labelsState['lv_plan_btn_enterprise'] || 'Talk to sales') : (labelsState['lv_plan_btn_pro'] || 'Try free for 14 days') }}
          </a>
        </article>
      </div>

      <div v-else class="pricing-grid">
        <article class="plan-v2">
          <div class="plan-v2-name">Start</div>
          <div class="plan-v2-tag">{{ labelsState['lv_plan_tag_free'] || 'To get started' }}</div>
          <div class="plan-v2-price">
            <span class="currency">$</span>
            <span class="amount">{{ annual ? 39 : 49 }}</span>
            <span class="period">{{ labelsState['lv_plan_period'] || '/mo USD' }}</span>
          </div>
          <ul>
            <li><svg class="check-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" width="16" height="16"><path d="M20 6L9 17l-5-5"/></svg>{{ labelsState['lv_plan_start_item_one'] || '1,000 AI messages / month' }}</li>
            <li><svg class="check-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" width="16" height="16"><path d="M20 6L9 17l-5-5"/></svg>{{ labelsState['lv_plan_start_item_two'] || '1 channel of your choice' }}</li>
            <li><svg class="check-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" width="16" height="16"><path d="M20 6L9 17l-5-5"/></svg>{{ labelsState['lv_plan_start_item_three'] || 'Basic dashboard' }}</li>
            <li><svg class="check-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" width="16" height="16"><path d="M20 6L9 17l-5-5"/></svg>{{ labelsState['lv_plan_start_item_four'] || 'Email support' }}</li>
          </ul>
          <a class="btn btn-ghost" :href="subsUrl" target="_blank">{{ labelsState['lv_plan_btn_free'] || 'Start free' }}</a>
        </article>
        <article class="plan-v2 featured">
          <div class="plan-v2-name">Pro</div>
          <div class="plan-v2-tag">{{ labelsState['lv_plan_tag_popular'] || 'Most popular' }}</div>
          <div class="plan-v2-price">
            <span class="currency">$</span>
            <span class="amount">{{ annual ? 119 : 149 }}</span>
            <span class="period">{{ labelsState['lv_plan_period'] || '/mo USD' }}</span>
          </div>
          <ul>
            <li><svg class="check-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" width="16" height="16"><path d="M20 6L9 17l-5-5"/></svg>{{ labelsState['lv_plan_pro_item_one'] || '10,000 AI messages / month' }}</li>
            <li><svg class="check-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" width="16" height="16"><path d="M20 6L9 17l-5-5"/></svg>{{ labelsState['lv_plan_all_channels'] || 'All channels' }}</li>
            <li><svg class="check-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" width="16" height="16"><path d="M20 6L9 17l-5-5"/></svg>{{ labelsState['lv_plan_pro_item_three'] || 'Human handover + unified inbox' }}</li>
            <li><svg class="check-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" width="16" height="16"><path d="M20 6L9 17l-5-5"/></svg>{{ labelsState['lv_plan_pro_item_four'] || 'Native integrations' }}</li>
            <li><svg class="check-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" width="16" height="16"><path d="M20 6L9 17l-5-5"/></svg>{{ labelsState['lv_plan_pro_item_five'] || 'Priority support' }}</li>
          </ul>
          <a class="btn btn-primary" :href="subsUrl" target="_blank">{{ labelsState['lv_plan_btn_pro'] || 'Try free for 14 days' }}</a>
        </article>
        <article class="plan-v2">
          <div class="plan-v2-name">Scale</div>
          <div class="plan-v2-tag">{{ labelsState['lv_plan_tag_enterprise'] || 'For large teams' }}</div>
          <div class="plan-v2-price">
            <span class="amount" style="font-size:42px">{{ labelsState['lv_plan_enterprise_price'] || 'Custom' }}</span>
          </div>
          <ul>
            <li><svg class="check-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" width="16" height="16"><path d="M20 6L9 17l-5-5"/></svg>{{ labelsState['lv_plan_scale_item_one'] || 'Unlimited volume' }}</li>
            <li><svg class="check-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" width="16" height="16"><path d="M20 6L9 17l-5-5"/></svg>{{ labelsState['lv_plan_scale_item_two'] || 'Dedicated models' }}</li>
            <li><svg class="check-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" width="16" height="16"><path d="M20 6L9 17l-5-5"/></svg>{{ labelsState['lv_plan_sla'] || 'SLA 99.9% + dedicated support' }}</li>
            <li><svg class="check-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" width="16" height="16"><path d="M20 6L9 17l-5-5"/></svg>{{ labelsState['lv_plan_scale_item_four'] || 'Dedicated success manager' }}</li>
          </ul>
          <a class="btn btn-ghost" :href="subsUrl" target="_blank">{{ labelsState['lv_plan_btn_enterprise'] || 'Talk to sales' }}</a>
        </article>
      </div>
    </div>
  </section>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import axios from 'axios';
import { useLabelsStore } from '@/stores/labels';
import { useLanguageStore } from '@/stores/language';

export default {
  name: 'PricingV2',
  setup() {
    const labelsStore = useLabelsStore();
    const languageStore = useLanguageStore();
    const labelsState = computed(() => labelsStore.labels);
    const annual = ref(true);
    const loading = ref(true);
    const plans = ref([]);
    const subsUrl = `${process.env.VUE_APP_DASHBOARD_URL}/subscriptions`;

    const fetchPlans = async () => {
      try {
        const res = await axios.get(`${process.env.VUE_APP_HOST_BILLING}/plans_open`);
        const all = res.data.plans || [];
        const sorted = all.sort((a, b) => {
          if (a.is_free && !b.is_free) return -1;
          if (!a.is_free && b.is_free) return 1;
          if (a.is_enterprise && !b.is_enterprise) return 1;
          if (!a.is_enterprise && b.is_enterprise) return -1;
          return (a.amount || 0) - (b.amount || 0);
        });
        plans.value = sorted;
      } catch {
        plans.value = [];
      } finally {
        loading.value = false;
      }
    };

    onMounted(() => {
      labelsStore.fetchAndSetLabels(languageStore.language);
      fetchPlans();
    });

    const displayPlans = computed(() => {
      const all = plans.value;
      if (!all.length) return [];
      const freq = annual.value ? 12 : 1;
      const result = [];

      const freePlan = all.find(p => p.is_free);
      if (freePlan) result.push(freePlan);

      const paid = all
        .filter(p => !p.is_free && !p.is_enterprise && !p.is_infrastructure && p.frequency === freq)
        .sort((a, b) => (a.amount || 0) - (b.amount || 0));
      if (paid.length) result.push(paid[Math.floor(paid.length / 2)] || paid[0]);

      const enterprise = all.find(p => p.is_enterprise);
      if (enterprise) result.push(enterprise);

      return result.slice(0, 3);
    });

    function formatPrice(plan) {
      const base = annual.value && plan.discount_amount_usd ? plan.discount_amount_usd : plan.amount;
      if (!base) return '0';
      return base.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    }

    function formatNumber(value) {
      if (!value) return '0';
      return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    }

    return { annual, loading, displayPlans, subsUrl, formatPrice, formatNumber, labelsState };
  },
};
</script>
